import { useRef, useState } from 'react'
import { useDismiss } from '../lib/useDismiss'
import { useHubLang } from '../lib/i18n'
import { HOME } from '../lib/hubCopy'
import { PencilIcon, StarIcon } from './icons'

/**
 * The post buttons, reachable from anywhere in a long feed. David, 12 Oct:
 * "otherwise when the feed is long, someone would have to scroll back to the
 * top of the page to make a post."
 *
 * ── IT IS THE ONLY POST CONTROL NOW, AND IT IS ALWAYS THERE ─────────────────
 *
 * First built (12 Oct) to appear only once the row of buttons above the feed
 * (PostBar) had scrolled away. The same evening David took that row out —
 * "the new pencil icon is great and is all that's needed" — so this shows on
 * every load, costs the feed no space at all (it floats), and is the one door
 * onto the composer. 20 Sep's reason for removing the permanent composer —
 * feed space for ~900 people who mostly read — is better served than before.
 *
 * ── THE SAME HANDLERS, NEVER A SECOND FORM ──────────────────────────────────
 *
 * Every item calls the route's own function: one composer dialog, one give
 * dialog (the rail's Star One card opens the same one), one drafts picker
 * (FED-35, 059's rule). A reader
 * who may not post gets Recognize alone, and then the button IS that act — a
 * menu with one row in it is a question with one answer.
 *
 * ── WHERE IT SITS ───────────────────────────────────────────────────────────
 *
 * Below 820px, bottom right, just above the tab bar. From 820px, stacked above
 * the Ask One bubble, which owns the corner at that width. Both are in
 * hub.css at `.postfab`.
 */
export function PostFab({
  onRecognize,
  onPost,
  onDrafts,
}: {
  onRecognize: () => void
  /** Absent for somebody who may not post (or in the employee view). */
  onPost?: () => void
  onDrafts?: () => void
}) {
  const h = HOME[useHubLang()]
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(ref, open, () => setOpen(false))

  const items: { key: string; label: string; run: () => void }[] = []
  if (onPost) items.push({ key: 'post', label: h.postSomething, run: onPost })
  items.push({ key: 'recognize', label: h.recognizeColleague, run: onRecognize })
  if (onDrafts) items.push({ key: 'drafts', label: h.yourDrafts, run: onDrafts })
  const single = items.length === 1

  function choose(run: () => void) {
    setOpen(false)
    run()
  }

  return (
    <div ref={ref} className="postfab">
      {open && !single && (
        <ul className="postfab__menu" role="menu" aria-label={h.postFabLabel}>
          {items.map((it) => (
            <li key={it.key} role="none">
              <button type="button" role="menuitem" className="postfab__item" onClick={() => choose(it.run)}>
                {it.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="postfab__btn"
        aria-label={single ? items[0].label : h.postFabLabel}
        aria-haspopup={single ? undefined : 'menu'}
        aria-expanded={single ? undefined : open}
        onClick={() => (single ? items[0].run() : setOpen((o) => !o))}
      >
        {single ? <StarIcon size={22} /> : <PencilIcon size={22} />}
      </button>
    </div>
  )
}
